import axios, { AxiosResponse } from "axios";
import { environment } from "../../../../environments/environment";
import { PdfFormListing, PdfFormSchema, PdfFormSubmission } from "../../../pages/forms-page/models/pdf-form-schema.types";

//@ts-ignore
const qs = require('qs');
  /**
   * List all PDF form templates available in Strapi. The schema JSON is
   * intentionally NOT requested here — the index only needs a title and
   * field count for the picker. `loadPdfFormSchema` fetches the heavy
   * `schema` blob (and the file URL) for the one the user actually opens.
   */
export const  findPdfForms =  async (): Promise<PdfFormListing[]>  =>{
    return new Promise((resolve) => {
      const queryString = qs.stringify({
        filters: { isActive: { $eq: true } },
        fields: ['slug', 'title', 'schema'],
        populate: { file: { fields: ['url'] } },
        sort: ['title:asc'],
        pagination: { pageSize: 200, page: 1 },
      }, { encodeValuesOnly: true });

      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${environment.apiUrl}api/pdf-forms?${queryString}`,
        headers: { 'Accept': 'application/json' },
      };

      axios(config)
        .then((response: AxiosResponse) => {
          const rows = response.data?.data ?? [];
          resolve(rows.map((r: any) => toPdfFormListing(r)).filter(Boolean));
        })
        .catch((error) => {
          console.devlog('findPdfForms error:', error);
          resolve([]);
        });
    });
  }

  /**
   * Fetch a single pdf-form by its slug, returning the enriched schema with
   * `pdfPath` set to the absolute media URL so the viewer + pdf-lib download
   * paths can fetch the bytes directly. Returns null on miss/error.
   */
export const loadPdfFormSchema = async (slug: string): Promise<PdfFormSchema | null> =>{
    return new Promise((resolve) => {
      const queryString = qs.stringify({
        filters: { slug: { $eq: slug } },
        populate: { file: { fields: ['url'] } },
        pagination: { pageSize: 1, page: 1 },
      }, { encodeValuesOnly: true });

      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${environment.apiUrl}api/pdf-forms?${queryString}`,
        headers: { 'Accept': 'application/json' },
      };

      axios(config)
        .then((response: AxiosResponse) => {
          const row = response.data?.data?.[0];
          resolve(row ? toPdfFormSchema(row) : null);
        })
        .catch((error) => {
          console.devlog('loadPdfFormSchema error:', error);
          resolve(null);
        });
    });
  }

  /** Resolve a Strapi media URL — local provider returns paths like
   *  `/uploads/foo.pdf` (need apiUrl prefix); GCS/cloud providers return
   *  fully-qualified `https://…` URLs (use as-is). */
export const absolutizeMediaUrl = (url: string | undefined | null): string =>{
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    const base = environment.apiUrl.replace(/\/+$/, '');
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
  }

export const toPdfFormListing = (row: any): PdfFormListing | null => {
    if (!row) return null;
    // strapi v5 migration: working directly on the data which should be flattened now
    // const attrs = row.attributes ?? row;
    // const fileAttrs = attrs.file?.data?.attributes ?? attrs.file?.data ?? attrs.file ?? {};
    const attrs = row;
    const fileAttrs = attrs.file?.data ?? attrs.file ?? {};
    const slug = attrs.slug;
    if (!slug) return null;
    return {
      slug,
      title: attrs.title ?? slug,
      fileUrl: absolutizeMediaUrl(fileAttrs.url),
      fieldCount: Array.isArray(attrs.schema?.fields) ? attrs.schema.fields.length : 0,
    };
  }

export const  toPdfFormSchema = (row: any): PdfFormSchema | null => {
    if (!row) return null;
    // strapi v5 migration: working directly on the data which should be flattened now
    // const attrs = row.attributes ?? row;
    const attrs = row;
    const schema = attrs.schema;
    if (!schema || !Array.isArray(schema.fields)) return null;
    // strapi v5 migration: working directly on the data which should be flattened now
    // const fileAttrs = attrs.file?.data?.attributes ?? attrs.file?.data ?? attrs.file ?? {};
    const fileAttrs = attrs.file?.data ?? attrs.file ?? {};
    return {
      ...schema,
      pdfFormId: row.id,
      title: attrs.title ?? schema.title,
      pdfPath: absolutizeMediaUrl(fileAttrs.url),
    };
  }

  /**
   * Find the latest saved fill of a given PDF form for a given patient.
   * Returns null when no submission exists, when the patient relation is
   * not allowed, or on any network/Strapi error — the prefill flow uses
   * a null return as the "fall back to pinned-user context" signal, so
   * we deliberately don't reject here.
   */
export const findLatestFormSubmission = async (slug: string, patientId: number | string | null): Promise<PdfFormSubmission | null> => {
    return new Promise((resolve) => {
      const filters: Record<string, unknown> = { pdfForm: { slug: { $eq: slug } } };
      if (patientId !== null && patientId !== undefined) {
        filters['patient'] = { id: { $eq: patientId } };
      } else {
        filters['patient'] = { id: { $null: true } };
      }
      const queryString = qs.stringify({
        filters,
        sort: ['updatedAt:desc'],
        pagination: { pageSize: 1, page: 1 },
        populate: ['patient', 'pdfForm'],
      }, {
        encodeValuesOnly: true,
      });

      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${environment.apiUrl}api/form-submissions/find-checked?${queryString}`,
        headers: {
          'Accept': 'application/json',
        },
      };

      axios(config)
        .then((response: AxiosResponse) => {
          const row = response.data?.data?.[0];
          resolve(row ? normalizeFormSubmission(row) : null);
        })
        .catch((error) => {
          console.devlog('findLatestFormSubmission error:', error);
          resolve(null);
        });
    });
  }

  /**
   * Upsert a form submission. POST when `submission.id` is unset, PUT
   * otherwise — the same instance is reused across saves so we never
   * accumulate duplicate rows for the same (pdfId, patient) pair.
   * Strapi v4 wraps every write payload in `{ data: ... }`.
   */
export const saveFormSubmission = async (submission: PdfFormSubmission): Promise<PdfFormSubmission> => {
    return new Promise((resolve, reject) => {
      const payload = {
        data: {
          pdfForm: submission.pdfFormId,
          patient: submission.patientId ?? null,
          values: submission.values ?? {},
          custom: submission.custom ?? {},
        },
      };

      console.devlog("Data to be saved for form submission:", payload);

      const isUpdate = submission.id !== undefined && submission.id !== null;
      const url = isUpdate
        ? `${environment.apiUrl}api/form-submissions/update-checked/${submission.id}`
        : `${environment.apiUrl}api/form-submissions/create-checked`;

      const config = {
        method: isUpdate ? 'put' : 'post',
        maxBodyLength: Infinity,
        url,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        data: payload,
      };

      axios(config)
        .then((response: AxiosResponse) => {
          resolve(normalizeFormSubmission(response.data?.data));
        })
        .catch((error) => {
          console.devlog('saveFormSubmission error:', error);
          reject(error);
        });
    });
  }

  /**
   * Flatten a Strapi v4 form-submission row (fields nested under
   * `attributes`, relations under `data`) into the flat
   * `PdfFormSubmission` shape the forms page works with.
   * Tolerant of both wrapped and already-flat shapes since Strapi's
   * response format varies by endpoint/version.
   */
export const normalizeFormSubmission = (row: any): PdfFormSubmission =>{
    if (!row) return null as any;
    // strapi v5 migration: working directly on the data which should be flattened now
    // const attrs = row.attributes ?? row;
    // const pdfFormAttrs = attrs.file?.data?.attributes ?? attrs.file?.data ?? attrs.file ?? {};
    const attrs = row;
    const patient = attrs.patient?.data?.id ?? attrs.patient?.id ?? attrs.patient ?? null;
    const pdfFormAttrs = attrs.file?.data ?? attrs.file ?? {};
    const pdfFormId = attrs.pdfForm?.data?.id ?? attrs.pdfForm?.id ?? null;
    return {
      id: row.id,
      pdfFormId,
      slug: pdfFormAttrs.slug ?? null,
      patientId: patient,
      values: attrs.values ?? {},
      custom: attrs.custom ?? {},
      createdAt: attrs.createdAt,
      updatedAt: attrs.updatedAt,
    };
  }